package fr.frux.feature.harvests

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Campaign
import androidx.compose.material.icons.outlined.Groups
import androidx.compose.material.icons.outlined.Home
import androidx.compose.material.icons.outlined.Park
import androidx.compose.material.icons.outlined.ShoppingBasket
import androidx.compose.material3.Icon
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.unit.dp
import fr.frux.app.resources.Res
import fr.frux.app.resources.harvests_domain_note
import fr.frux.app.resources.harvests_find_tree_cta
import fr.frux.app.resources.harvests_full
import fr.frux.app.resources.harvests_hosted_by_owner
import fr.frux.app.resources.harvests_intro
import fr.frux.app.resources.harvests_open
import fr.frux.app.resources.harvests_participants_cueilleur_plural
import fr.frux.app.resources.harvests_participants_cueilleur_singular
import fr.frux.app.resources.harvests_participants_with_max
import fr.frux.app.resources.harvests_place_empty_hint
import fr.frux.app.resources.harvests_step1_body
import fr.frux.app.resources.harvests_step1_title
import fr.frux.app.resources.harvests_step2_body
import fr.frux.app.resources.harvests_step2_title
import fr.frux.app.resources.harvests_step3_body
import fr.frux.app.resources.harvests_step3_title
import fr.frux.app.resources.harvests_subtitle
import fr.frux.app.resources.harvests_title
import fr.frux.app.resources.harvests_zone_map_center
import fr.frux.app.resources.harvests_zone_zoomed_out
import fr.frux.core.designsystem.components.ErrorState
import fr.frux.core.designsystem.components.FruxButton
import fr.frux.core.designsystem.components.FruxButtonStyle
import fr.frux.core.designsystem.components.FruxCard
import fr.frux.core.designsystem.components.FruxScreenHeader
import fr.frux.core.designsystem.components.LoadingState
import fr.frux.core.designsystem.components.LocalBottomNavPadding
import fr.frux.core.designsystem.error.userMessage
import fr.frux.core.designsystem.theme.FruxSpacing
import fr.frux.core.designsystem.theme.fruxColors
import fr.frux.feature.map.SeasonalAroundSection
import fr.frux.shared.domain.model.SeasonalAround
import fr.frux.shared.domain.model.Species
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import org.jetbrains.compose.resources.stringResource
import kotlin.time.ExperimentalTime
import kotlin.time.Instant

/**
 * Récoltes — liste des récoltes à venir à proximité (#166).
 *
 * Pas de zone morte pour l'état vide ni pour l'absence de position (permission refusée, GPS
 * coupé) : les deux rendent le même corps pédagogique qu'avant cette liste réelle — il explique
 * ce qu'est une récolte collective et renvoie vers la carte, plutôt que de laisser un écran nu.
 * Depuis #412, ils portent aussi la seule issue réelle de l'impasse : chercher un lieu.
 *
 * @param onHarvestClick Ouvre la fiche d'une récolte.
 * @param onSeasonSpeciesClick Tap sur une pastille « De saison en ce moment » : ouvre la carte
 *   filtrée sur cette espèce.
 * @param mapSeasonalAround Version croisée avec les arbres de la carte persistante (#403), fournie
 *   par l'appelant (ADR 0009 : la carte est ailleurs dans l'arbre de composition). `null` tant
 *   qu'aucune carte n'a jamais été composée : le repli reste alors la liste nationale du
 *   [HarvestsViewModel] lui-même.
 */
@Composable
fun HarvestsScreen(
    onExploreMap: () -> Unit,
    onHarvestClick: (Long) -> Unit = {},
    onSeasonSpeciesClick: (Species) -> Unit = {},
    mapSeasonalAround: SeasonalAround? = null,
    viewModel: HarvestsViewModel,
) {
    val state by viewModel.state.collectAsState()
    val bottomNavPadding = LocalBottomNavPadding.current
    val seasonalAround = mapSeasonalAround ?: state.seasonalAround

    Scaffold(
        containerColor = MaterialTheme.colorScheme.background,
    ) { paddingValues ->
        Column(modifier = Modifier.fillMaxSize().padding(paddingValues)) {
            FruxScreenHeader(
                title = stringResource(Res.string.harvests_title),
                subtitle = stringResource(Res.string.harvests_subtitle),
            )
            // Le champ de lieu (#412) reste au-dessus de tous les états : choisir un lieu recentre
            // la carte, et la liste suit (#534).
            HarvestsPlaceField(
                query = state.placeQuery,
                results = state.placeResults,
                selectedPlace = state.selectedPlace,
                onQueryChange = viewModel::onPlaceQueryChange,
                onPlaceClick = viewModel::selectPlace,
                onClearPlace = viewModel::clearSelectedPlace,
                modifier = Modifier.padding(horizontal = FruxSpacing.md),
            )
            Spacer(modifier = Modifier.height(FruxSpacing.sm))
            SeasonalAroundSection(
                data = seasonalAround,
                onSpeciesClick = onSeasonSpeciesClick,
                modifier = Modifier.fillMaxWidth().padding(horizontal = FruxSpacing.md),
            )
            // Le lieu choisi se nomme déjà dans le champ au-dessus. Sans lui, la zone est le centre
            // de la carte, et rien d'autre ne le dirait à l'écran (#534).
            if (state.selectedPlace == null) {
                Text(
                    text =
                        if (state.mapZoomedOut) {
                            stringResource(Res.string.harvests_zone_zoomed_out)
                        } else {
                            stringResource(Res.string.harvests_zone_map_center)
                        },
                    style = MaterialTheme.typography.labelLarge,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    modifier = Modifier.padding(horizontal = FruxSpacing.md, vertical = FruxSpacing.xs),
                )
            }
            Box(modifier = Modifier.weight(1f)) {
                when {
                    state.isLoading -> {
                        LoadingState(
                            modifier = Modifier.padding(bottom = bottomNavPadding),
                        )
                    }

                    state.error != null -> {
                        ErrorState(
                            message = state.error!!.userMessage(),
                            onRetry = viewModel::load,
                            modifier = Modifier.padding(bottom = bottomNavPadding),
                        )
                    }

                    state.harvests.isEmpty() -> {
                        HarvestsPedagogicalContent(
                            onExploreMap = onExploreMap,
                            hint = stringResource(Res.string.harvests_place_empty_hint),
                        )
                    }

                    else -> {
                        LazyColumn(
                            modifier = Modifier.fillMaxSize(),
                            contentPadding =
                                PaddingValues(
                                    start = FruxSpacing.md,
                                    end = FruxSpacing.md,
                                    top = FruxSpacing.md,
                                    bottom = bottomNavPadding,
                                ),
                            verticalArrangement = Arrangement.spacedBy(FruxSpacing.sm),
                        ) {
                            items(state.harvests, key = { it.id }) { harvest ->
                                HarvestListCard(harvest = harvest, onClick = { onHarvestClick(harvest.id) })
                            }
                        }
                    }
                }
            }
        }
    }
}

/**
 * Corps pédagogique historique de l'écran (avant #166) : sert désormais l'état vide.
 */
@Composable
private fun HarvestsPedagogicalContent(
    onExploreMap: () -> Unit,
    hint: String,
) {
    Column(
        modifier =
            Modifier
                .fillMaxSize()
                .verticalScroll(rememberScrollState())
                .padding(bottom = LocalBottomNavPadding.current),
    ) {
        Column(
            modifier = Modifier.padding(horizontal = FruxSpacing.md),
            verticalArrangement = Arrangement.spacedBy(FruxSpacing.sm),
        ) {
            Text(
                text = hint,
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurface,
            )
            Text(
                text = stringResource(Res.string.harvests_intro),
                style = MaterialTheme.typography.bodyMedium,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
            Spacer(modifier = Modifier.height(FruxSpacing.sm))

            HarvestStep(
                icon = Icons.Outlined.Park,
                title = stringResource(Res.string.harvests_step1_title),
                body = stringResource(Res.string.harvests_step1_body),
            )
            HarvestStep(
                icon = Icons.Outlined.Campaign,
                title = stringResource(Res.string.harvests_step2_title),
                body = stringResource(Res.string.harvests_step2_body),
            )
            HarvestStep(
                icon = Icons.Outlined.ShoppingBasket,
                title = stringResource(Res.string.harvests_step3_title),
                body = stringResource(Res.string.harvests_step3_body),
            )

            Spacer(modifier = Modifier.height(FruxSpacing.sm))
            Text(
                text = stringResource(Res.string.harvests_domain_note),
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )

            Spacer(modifier = Modifier.height(FruxSpacing.md))
            FruxButton(
                text = stringResource(Res.string.harvests_find_tree_cta),
                onClick = onExploreMap,
                style = FruxButtonStyle.Filled,
                modifier = Modifier.fillMaxWidth(),
            )
            Spacer(modifier = Modifier.height(FruxSpacing.md))
        }
    }
}

/**
 * Icône vectorielle plutôt qu'un emoji texte : sur web (js), les emojis ne sont pas rendus par
 * Compose et laissaient un carré vide — un tiers de la surface de cet écran à trois cartes. Une
 * icône Material est dessinée par Compose sur toutes les cibles, et purement décorative
 * (`contentDescription = null`) puisque le titre à côté porte déjà le sens de la carte.
 */
@Composable
private fun HarvestStep(
    icon: ImageVector,
    title: String,
    body: String,
) {
    FruxCard(modifier = Modifier.fillMaxWidth()) {
        Row(verticalAlignment = Alignment.Top) {
            Icon(
                imageVector = icon,
                contentDescription = null,
                tint = MaterialTheme.colorScheme.primary,
                modifier = Modifier.size(28.dp),
            )
            Spacer(modifier = Modifier.width(FruxSpacing.md))
            Column {
                Text(text = title, style = MaterialTheme.typography.titleMedium)
                Text(
                    text = body,
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }
        }
    }
}

@Composable
private fun HarvestListCard(
    harvest: HarvestListItem,
    onClick: () -> Unit,
) {
    FruxCard(modifier = Modifier.fillMaxWidth(), onClick = onClick) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            DatePastille(epochMillis = harvest.scheduledDate)
            Spacer(modifier = Modifier.width(FruxSpacing.md))
            Column(modifier = Modifier.weight(1f)) {
                if (harvest.hostedByOwner) {
                    HostedByOwnerChip()
                }
                Text(text = harvest.title, style = MaterialTheme.typography.titleMedium)
                Text(
                    text = harvest.meta,
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }
            Spacer(modifier = Modifier.width(FruxSpacing.sm))
            OpenOrFullChip(isFull = harvest.isFull)
        }
        Spacer(modifier = Modifier.height(FruxSpacing.sm))
        Row(verticalAlignment = Alignment.CenterVertically) {
            Icon(
                imageVector = Icons.Outlined.Groups,
                contentDescription = null,
                tint = MaterialTheme.colorScheme.primary,
                modifier = Modifier.size(18.dp),
            )
            Spacer(modifier = Modifier.width(FruxSpacing.xs))
            Text(
                text =
                    harvest.maxParticipants?.let { max ->
                        stringResource(Res.string.harvests_participants_with_max, harvest.participantsCount, max)
                    } ?: cueilleursLabel(harvest.participantsCount),
                style = MaterialTheme.typography.bodySmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        }
        val maxParticipants = harvest.maxParticipants
        if (maxParticipants != null) {
            Spacer(modifier = Modifier.height(FruxSpacing.xs))
            LinearProgressIndicator(
                progress = { harvest.participantsCount.toFloat() / maxParticipants },
                modifier = Modifier.fillMaxWidth(),
            )
        }
    }
}

@Composable
private fun cueilleursLabel(count: Int): String =
    if (count <= 1) {
        stringResource(Res.string.harvests_participants_cueilleur_singular, count)
    } else {
        stringResource(Res.string.harvests_participants_cueilleur_plural, count)
    }

/** Pastille mois abrégé + jour, seul endroit de l'app qui a besoin d'un libellé de mois court. */
@OptIn(ExperimentalTime::class)
@Composable
private fun DatePastille(epochMillis: Long) {
    val dt = Instant.fromEpochMilliseconds(epochMillis).toLocalDateTime(TimeZone.currentSystemDefault())
    Column(
        modifier =
            Modifier
                .size(48.dp)
                .clip(RoundedCornerShape(12.dp))
                .background(MaterialTheme.colorScheme.primaryContainer),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center,
    ) {
        Text(
            text = monthAbbreviation(dt.monthNumber),
            style = MaterialTheme.typography.labelSmall,
            color = MaterialTheme.colorScheme.onPrimaryContainer,
        )
        Text(
            text = dt.day.toString(),
            style = MaterialTheme.typography.titleMedium,
            color = MaterialTheme.colorScheme.onPrimaryContainer,
        )
    }
}

private fun monthAbbreviation(monthNumber: Int): String =
    when (monthNumber) {
        1 -> "JANV"
        2 -> "FÉVR"
        3 -> "MARS"
        4 -> "AVR"
        5 -> "MAI"
        6 -> "JUIN"
        7 -> "JUIL"
        8 -> "AOÛT"
        9 -> "SEPT"
        10 -> "OCT"
        11 -> "NOV"
        else -> "DÉC"
    }

/** Pastille « Chez moi » (ADR 0035) : le propriétaire du lieu accueille lui-même. */
@Composable
private fun HostedByOwnerChip(modifier: Modifier = Modifier) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
        modifier =
            modifier
                .padding(bottom = FruxSpacing.xs)
                .clip(RoundedCornerShape(50))
                .background(MaterialTheme.colorScheme.tertiaryContainer)
                .padding(horizontal = 8.dp, vertical = 3.dp),
    ) {
        Icon(
            imageVector = Icons.Outlined.Home,
            contentDescription = null,
            tint = MaterialTheme.colorScheme.onTertiaryContainer,
            modifier = Modifier.size(14.dp),
        )
        Spacer(modifier = Modifier.width(FruxSpacing.xs))
        Text(
            text = stringResource(Res.string.harvests_hosted_by_owner),
            style = MaterialTheme.typography.labelMedium,
            color = MaterialTheme.colorScheme.onTertiaryContainer,
        )
    }
}

@Composable
private fun OpenOrFullChip(
    isFull: Boolean,
    modifier: Modifier = Modifier,
) {
    val color = if (isFull) MaterialTheme.fruxColors.statusUnavailable else MaterialTheme.fruxColors.statusAvailable
    Text(
        text = if (isFull) stringResource(Res.string.harvests_full) else stringResource(Res.string.harvests_open),
        style = MaterialTheme.typography.labelMedium,
        color = color,
        modifier =
            modifier
                .clip(RoundedCornerShape(50))
                .background(color.copy(alpha = 0.14f))
                .padding(horizontal = 10.dp, vertical = 4.dp),
    )
}
